import { MockServiceStore } from "./MockServiceStore";
import { mockStructureCastComponents } from "./structurecast-components.mock";
import { mockStructureCastCastingDays } from "./structurecast-casting-days.mock";

/**
 * StructureCast - Mold Assignment (component -> mold slot on a day)
 */
export interface StructureCastMoldAssignment {
  id: string;
  castingDayId: string;
  componentId: string;
  moldPosition: 1 | 2 | 3 | 4 | 5 | "sump";
  cubicYards: number;
  weight: number;
  status: "planned" | "poured" | "stripped" | "cured";
  castingTableId: string;
  yardZoneId: string;
  shipmentLoadId: string;
  createdAt: string;
  updatedAt: string;
}

// Build lookup from casting day ID to group ID
const dayToGroupId = new Map<string, string>();
for (const day of mockStructureCastCastingDays) {
  dayToGroupId.set(day.id, day.castingGroupId);
}

// Pre-assign completed Group 1 (Heritage Ranch — Deep Manholes) to tables for realistic floor plan data
const completedGroupTableMap: Record<string, string[]> = {
  "sc-cg-1": ["sc-ct-1", "sc-ct-2", "sc-ct-4"],
};

// Pre-assign completed group to yard zones for storage yard data
const completedGroupYardZoneMap: Record<string, string[]> = {
  "sc-cg-1": ["sc-yz-1", "sc-yz-2", "sc-yz-7"],
};

// Pre-assign some cured pieces to shipment loads
const completedGroupShipmentLoadMap: Record<string, string[]> = {
  "sc-cg-1": ["sc-load-1"],
};

const generateMoldAssignments = (): StructureCastMoldAssignment[] => {
  const assignments: StructureCastMoldAssignment[] = [];
  let assignId = 1;

  const scheduledComponents = mockStructureCastComponents.filter(
    (c) => c.castingDayId !== ""
  );

  for (const comp of scheduledComponents) {
    const moldPos = comp.moldPosition;

    let status: StructureCastMoldAssignment["status"] = "planned";
    if (comp.status === "cured") status = "cured";
    else if (comp.status === "cast") status = "stripped";
    else if (comp.status === "scheduled") status = "planned";

    // Pre-populate castingTableId for completed groups
    let castingTableId = "";
    const groupId = dayToGroupId.get(comp.castingDayId);
    if (groupId && completedGroupTableMap[groupId]) {
      const tables = completedGroupTableMap[groupId];
      castingTableId = tables[(assignId - 1) % tables.length];
    }

    // Pre-populate yardZoneId for completed groups
    let yardZoneId = "";
    if (groupId && completedGroupYardZoneMap[groupId] && (status === "cured" || status === "stripped")) {
      const zones = completedGroupYardZoneMap[groupId];
      yardZoneId = zones[(assignId - 1) % zones.length];
    }

    // Pre-populate shipmentLoadId for some cured pieces
    let shipmentLoadId = "";
    if (groupId && completedGroupShipmentLoadMap[groupId] && status === "cured" && yardZoneId) {
      const loads = completedGroupShipmentLoadMap[groupId];
      shipmentLoadId = loads[(assignId - 1) % loads.length];
    }

    assignments.push({
      id: `sc-ma-${String(assignId).padStart(3, "0")}`,
      castingDayId: comp.castingDayId,
      componentId: comp.id,
      moldPosition: typeof moldPos === "number" ? (moldPos as 1 | 2 | 3 | 4 | 5) : "sump",
      cubicYards: comp.cubicYards,
      weight: comp.weight,
      status,
      castingTableId,
      yardZoneId,
      shipmentLoadId,
      createdAt: "2025-11-15T00:00:00.000Z",
      updatedAt: "2026-01-19T00:00:00.000Z",
    });

    assignId++;
  }

  return assignments;
};

export const mockStructureCastMoldAssignments: StructureCastMoldAssignment[] = generateMoldAssignments();

export const createMockStructureCastMoldAssignmentStore = () =>
  new MockServiceStore<StructureCastMoldAssignment>({
    mockData: mockStructureCastMoldAssignments,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-mold-assignments",
  });

export default mockStructureCastMoldAssignments;
